import { fetchJSON as fetchAPI } from './client';

export type CatalogOpsDashboard = {
  tenant_id: string;
  total_listings: number;
  active_listings: number;
  suppressed_listings: number;
  draft_listings: number;
  variation_families: number;
  avg_health_score: number;
  compliance_issues: number;
  active_recommendations: number;
  active_bulk_jobs: number;
  generated_at?: string;
};

export type ListingRecord = {
  id: string;
  asin?: string;
  sku: string;
  title: string;
  brand?: string;
  category?: string;
  marketplace?: string;
  status: string;
  is_parent: boolean;
  parent_sku?: string;
  health_score: number;
  attribute_completeness_pct: number;
  version: number;
  requires_approval: boolean;
  updated_at?: string;
};

export type VariationRecord = {
  id: string;
  parent_sku: string;
  parent_asin?: string;
  theme?: string;
  child_count: number;
  child_skus?: string[];
  health_score: number;
  is_valid: boolean;
  issues?: string[];
};

export type AttributeRecord = {
  id: string;
  listing_id: string;
  name: string;
  value?: string;
  is_required: boolean;
  is_complete: boolean;
  category?: string;
};

export type ListingHealthRecord = {
  id: string;
  listing_id: string;
  sku: string;
  health_score: number;
  issue_type?: string;
  issue?: string;
  severity?: string;
};

export type CatalogRecommendation = {
  id: string;
  type: string;
  title: string;
  description?: string;
  sku?: string;
  asin?: string;
  expected_impact?: string;
  confidence_score: number;
  priority_rank: number;
  requires_approval: boolean;
  source_module?: string;
};

export type BulkJobRecord = {
  id: string;
  operation: string;
  status: string;
  total_items: number;
  processed_items: number;
  failed_items: number;
  created_at?: string;
};

export type ComplianceIssue = {
  id: string;
  listing_id: string;
  sku: string;
  rule: string;
  description?: string;
  severity?: string;
  resolved: boolean;
};

export type CreateListingRequest = {
  sku: string;
  title: string;
  brand?: string;
  category?: string;
  marketplace?: string;
  is_parent?: boolean;
  parent_sku?: string;
};

const API_BASE = import.meta.env.VITE_CATALOGOPS_API ?? '/api/v1/catalogops';

function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const catalogopsApi = {
  dashboard: () => fetchJSON<CatalogOpsDashboard>('/dashboard'),
  listListings: () => fetchJSON<ListingRecord[]>('/listings'),
  createListing: (body: CreateListingRequest) =>
    fetchJSON<ListingRecord>('/listings', { method: 'POST', body: JSON.stringify(body) }),
  listVariations: () => fetchJSON<VariationRecord[]>('/variations'),
  listAttributes: (listingId?: string) =>
    fetchJSON<AttributeRecord[]>(listingId ? `/attributes?listing_id=${listingId}` : '/attributes'),
  listHealth: () => fetchJSON<ListingHealthRecord[]>('/health'),
  listBulkJobs: () => fetchJSON<BulkJobRecord[]>('/bulk-jobs'),
  listRecommendations: () => fetchJSON<CatalogRecommendation[]>('/recommendations'),
  generateRecommendations: () =>
    fetchJSON<CatalogRecommendation[]>('/recommendations/generate', { method: 'POST' }),
  listCompliance: () => fetchJSON<ComplianceIssue[]>('/compliance'),
  listActivity: () => fetchJSON<{ id: string; message: string }[]>('/activity'),
};
